/**
 * @fileoverview Organism: the tracked purchases, as a horizontal-scrolling row.
 * @module components/organisms/PurchaseRow
 */

import { Skeleton } from "@/components/atoms"
import { EmptyState, PurchaseCard } from "@/components/molecules"
import type { Purchase, PurchaseId } from "@/types/types"

import "./PurchaseRow.css"

/** Props accepted by {@link PurchaseRow}. */
export interface PurchaseRowProps {
	/** The purchases to display, already filtered by category if any. */
	readonly purchases: readonly Purchase[]
	/** True until the first read settles. */
	readonly isLoading: boolean
	/** Opens the full detail, edit and delete modal for a purchase. */
	readonly onOpen: (id: PurchaseId) => void
	/** Records uses; a negative delta removes some. */
	readonly onRecordUse: (id: PurchaseId, delta: number) => void
}

/** Placeholder shaped like {@link PurchaseCard}, shown while loading. */
function PurchaseCardSkeleton() {
	return (
		<li className="purchase" aria-hidden="true">
			<Skeleton width="70%" height="20px" />
			<Skeleton width="45%" height="18px" radius="999px" />
			<Skeleton height="8px" radius="999px" />
			<Skeleton width="60%" height="14px" />
		</li>
	)
}

/**
 * Horizontal-scrolling strip of purchase cards.
 *
 * @param props - The purchases, loading state and card actions.
 * @returns The rendered row: skeleton cards while loading, an empty state
 *   with nothing tracked yet, or the cards themselves.
 */
export function PurchaseRow({
	purchases,
	isLoading,
	onOpen,
	onRecordUse,
}: PurchaseRowProps) {
	if (isLoading) {
		return (
			<div className="purchase-row">
				<ul
					className="purchase-row__list"
					aria-label="Achats en cours de chargement"
				>
					<PurchaseCardSkeleton />
					<PurchaseCardSkeleton />
					<PurchaseCardSkeleton />
				</ul>
			</div>
		)
	}

	if (purchases.length === 0) {
		return (
			<div className="purchase-row">
				<EmptyState
					title="Aucun achat suivi"
					message="Ajoutez un premier achat pour commencer à mesurer sa valeur d’usage."
				/>
			</div>
		)
	}

	return (
		<div className="purchase-row">
			<ul className="purchase-row__list" aria-label="Achats suivis">
				{purchases.map(purchase => (
					<PurchaseCard
						key={purchase.id}
						purchase={purchase}
						onOpen={onOpen}
						onRecordUse={onRecordUse}
					/>
				))}
			</ul>
		</div>
	)
}
